import * as React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Check, Copy, Download, FileCode2, FileOutput, Loader2, Printer, RotateCcw } from 'lucide-react';
import { api, ErroHttp } from '@/lib/api';
import { cn } from '@/lib/utils';
import { acaoTexto, Botao } from '@/componentes/ui/botao';
import { Esqueleto } from '@/componentes/ui/primitivos';
import { Rota } from './Rota';
import { moeda, type Destino, type DetalheNota, type ItemTransferencia, type PreviaTransferencia, type RastroTransferencia } from './tipos';

/**
 * NF de transferência pelo WMS (compra na 11, destino noutro CNPJ). Duas
 * caras na mesma seção:
 *   - antes de emitir: a NF montada ao vivo (itens, valor, CST, ICMS), com
 *     "Transferir a nota inteira" marcado; desmarcado, quantidade por item;
 *   - depois: o rastro — NF gravada → autorizada (DANFE/XML) → entrada no
 *     destino → recebimento.
 * A conta é do servidor (mesma regra do formulário 753537); a tela só pede.
 */

interface ParteDaNota {
  tipo: 'nf' | 'rascunho';
  destino: number;
  produto: number;
  quantidade: number;
  carga: number | null;
  cargaNome: string | null;
  nf: { numero: number | null; serie: string | null; status: string; chave: string | null; emitidaEm: string; entrada: number | null } | null;
}

interface DistribuicaoDaNota {
  itens: { produto: number; descricao: string | null; quantidadeNota: number; saldo: number }[];
  partes: ParteDaNota[];
}

interface RespostaRastro {
  rastro: RastroTransferencia | null;
  ligada: boolean;
  /** Para onde foi cada parte desta nota (gaveta e cargas). */
  distribuicao?: DistribuicaoDaNota | null;
}

interface RespostaPrevia extends RespostaRastro {
  previa: PreviaTransferencia;
  auto: boolean;
  podeEmitir: boolean;
}

const COM_TRANSFERENCIA = new Set(['aguardando_transferencia', 'agendada', 'em_transito_full', 'em_conferencia', 'divergencia_recebimento', 'estocada', 'disponivel_venda']);

/** O rastro da transferência desta nota (compartilhado com a ação da etapa). */
export function useRastroTransferencia(d: DetalheNota) {
  const n = d.nota;
  return useQuery<RespostaRastro>({
    queryKey: ['entrada-transf', n.chave],
    queryFn: () => api.get(`/api/entradas/nota/${n.chave}/transferencia-nf`),
    enabled: Boolean(n.destinoId) && COM_TRANSFERENCIA.has(n.status),
    // Esperando a SEFAZ ou a entrada no destino: confere de 15 em 15 s (o servidor
    // olha o PBS na hora); depois, para.
    refetchInterval: (q) => {
      const s = q.state.data?.rastro?.status;
      return s === 'EMITIDA' || s === 'AUTORIZADA' || s === 'DIVERGENTE' ? 15_000 : false;
    },
    refetchIntervalInBackground: false,
  });
}

export function TransferenciaNf({ d }: { d: DetalheNota }): JSX.Element | null {
  const r = useRastroTransferencia(d);
  if (!r.data) return null;
  const cargas = r.data.distribuicao?.partes.length ? <ParaOndeVai d={d} dist={r.data.distribuicao} /> : null;
  if (r.data.rastro) {
    return (
      <>
        <Rastro d={d} t={r.data.rastro} />
        {cargas}
      </>
    );
  }
  // Sempre em "Ag. transferência", ligada ou não: com a emissão desligada, a
  // prévia (integral ou parcial) serve de conferência para emitir no Procfit.
  if (d.nota.status === 'aguardando_transferencia') {
    return (
      <>
        {cargas}
        <Montagem d={d} />
      </>
    );
  }
  return cargas;
}

/**
 * Para onde vai esta nota: a NF de origem com o total, uma barra por destino
 * (o que já saiu, o que está em rascunho de carga e o saldo livre) e, parte a
 * parte, a NF de saída, o destino, a quantidade e onde está (emitida,
 * autorizada, entrada no destino). Junta gaveta e cargas: o saldo é um só.
 */
const CORES = ['bg-primaria', 'bg-sky-500', 'bg-emerald-500', 'bg-amber-500', 'bg-fuchsia-500', 'bg-rose-500'];

function ParaOndeVai({ d, dist }: { d: DetalheNota; dist: DistribuicaoDaNota }): JSX.Element | null {
  const destinos = useQuery<{ destinos: Destino[] }>({ queryKey: ['entradas-destinos'], queryFn: () => api.get('/api/entradas/destinos') });
  if (!dist.partes.length) return null;
  const nome = (id: number): string => {
    const x = destinos.data?.destinos.find((y) => y.id === id);
    return x ? `${x.nome} ${x.uf}` : `destino ${id}`;
  };
  const total = dist.itens.reduce((s, i) => s + i.quantidadeNota, 0);
  const saiu = dist.partes.filter((p) => p.tipo === 'nf').reduce((s, p) => s + p.quantidade, 0);
  const rascunho = dist.partes.filter((p) => p.tipo === 'rascunho').reduce((s, p) => s + p.quantidade, 0);
  const livre = Math.max(0, dist.itens.reduce((s, i) => s + i.saldo, 0) - rascunho);
  const ordemDestinos = [...new Set(dist.partes.map((p) => p.destino))];
  const cor = (destino: number): string => CORES[ordemDestinos.indexOf(destino) % CORES.length]!;
  const pct = (q: number): string => `${total ? (q / total) * 100 : 0}%`;
  const situacao = (p: ParteDaNota): { texto: string; tom: string } => {
    if (p.tipo === 'rascunho') return { texto: 'rascunho de carga', tom: 'bg-status-atencaoFundo text-status-atencao' };
    const s = p.nf!.status;
    if (s === 'ENTRADA') return { texto: `entrada no destino${p.nf!.entrada ? ` · NF compra ${p.nf!.entrada}` : ''}`, tom: 'bg-status-livreFundo text-status-livre' };
    if (s === 'AUTORIZADA') return { texto: 'autorizada na SEFAZ', tom: 'bg-sky-100 text-sky-700 dark:bg-sky-950 dark:text-sky-300' };
    if (s === 'DIVERGENTE') return { texto: 'entrada na empresa errada', tom: 'bg-status-divergenciaFundo text-status-divergencia' };
    return { texto: 'emitida · aguardando SEFAZ', tom: 'bg-superficie2 text-textoFraco' };
  };
  const variosItens = dist.itens.length > 1;

  return (
    <section className="space-y-3 rounded-xl border border-borda p-4">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <p className="rotulo">Para onde vai esta nota</p>
        <span className="text-xs text-textoFraco">
          NF {d.nota.numero} · <b className="text-texto">{total} un.</b>
          {saiu ? ` · ${saiu} saíram` : ''}
          {rascunho ? ` · ${rascunho} em rascunho` : ''} · {livre} livres
        </span>
      </div>

      {/* A nota inteira numa barra: cada destino uma cor; rascunho listrado; livre em cinza. */}
      <div className="flex h-3 overflow-hidden rounded-full bg-superficie2" role="img" aria-label="Distribuição da nota por destino">
        {dist.partes.map((p, k) => (
          <span
            key={k}
            className={cn('h-full', cor(p.destino), p.tipo === 'rascunho' && 'opacity-40')}
            style={{ width: pct(p.quantidade) }}
            title={`${p.quantidade} un. → ${nome(p.destino)}${p.tipo === 'rascunho' ? ' (rascunho)' : ` · NF ${p.nf?.numero ?? '…'}`}`}
          />
        ))}
      </div>

      <ol className="space-y-1.5">
        {dist.partes.map((p, k) => {
          const st = situacao(p);
          return (
            <li key={k} className="flex items-center gap-2.5 rounded-lg border border-borda px-3 py-2 text-[13px]">
              <span className={cn('h-8 w-1.5 shrink-0 rounded-full', cor(p.destino), p.tipo === 'rascunho' && 'opacity-40')} />
              <span className="min-w-0 flex-1">
                <span className="flex flex-wrap items-baseline gap-x-1.5">
                  <b className="tabular-nums">{p.quantidade} un.</b>
                  {variosItens ? <span className="text-xs text-textoFraco">do produto {p.produto}</span> : null}
                  <span className="text-textoSuave">→</span>
                  <b>{nome(p.destino)}</b>
                </span>
                <span className="block truncate text-2xs text-textoSuave">
                  {p.nf ? `NF de transferência ${p.nf.numero ?? '…'}${p.nf.serie ? `/${p.nf.serie}` : ''} · ${new Date(p.nf.emitidaEm).toLocaleDateString('pt-BR')}` : 'ainda sem NF'}
                  {p.carga ? ` · carga #${p.carga}${p.cargaNome ? ` (${p.cargaNome})` : ''}` : ' · pela nota'}
                </span>
              </span>
              <span className={cn('shrink-0 rounded-full px-2 py-0.5 text-2xs font-semibold', st.tom)}>{st.texto}</span>
              {p.carga ? (
                <a href={`/entradas?aba=carga&carga=${p.carga}`} className={cn(acaoTexto, 'shrink-0 text-2xs')}>
                  Abrir
                </a>
              ) : null}
            </li>
          );
        })}
        {livre > 0 ? (
          <li className="flex items-center gap-2.5 rounded-lg border border-dashed border-borda px-3 py-2 text-[13px] text-textoFraco">
            <span className="h-8 w-1.5 shrink-0 rounded-full bg-superficie2" />
            <span className="flex-1">
              <b className="tabular-nums text-texto">{livre} un.</b> livres — saldo para a próxima transferência
            </span>
            <a href="/entradas?aba=carga" className={cn(acaoTexto, 'shrink-0 text-2xs')}>
              Montar carga
            </a>
          </li>
        ) : null}
      </ol>
      <p className="text-2xs text-textoSuave">Cada emissão, autorização e entrada no destino também fica na linha do tempo desta nota.</p>
    </section>
  );
}

// ------------------------------------------------------------------ montagem

/** Valor com debounce: a prévia não dispara a cada tecla. */
function useAtrasado<T>(valor: T, ms: number): T {
  const [v, definir] = React.useState(valor);
  React.useEffect(() => {
    const t = setTimeout(() => definir(valor), ms);
    return () => clearTimeout(t);
  }, [valor, ms]);
  return v;
}

const num = (v: number, casas = 2): string => v.toLocaleString('pt-BR', { minimumFractionDigits: casas, maximumFractionDigits: casas });

function Montagem({ d }: { d: DetalheNota }): JSX.Element {
  const n = d.nota;
  const cliente = useQueryClient();
  const [inteira, definirInteira] = React.useState(true);
  const [qtds, definirQtds] = React.useState<Record<number, number>>({});
  const [valores, definirValores] = React.useState<Record<number, number>>({});
  const [valorBase, definirValorBase] = React.useState<'compra_ipi' | 'compra' | null>(null);
  const [confirmando, definirConfirmando] = React.useState(false);
  const itensConhecidos = React.useRef<ItemTransferencia[]>([]);

  // O pedido: nota inteira sem valor mexido = sem lista (o servidor usa tudo).
  const pedido = React.useMemo(() => {
    const mexeuValor = Object.keys(valores).length > 0;
    const itens =
      inteira && !mexeuValor
        ? null
        : itensConhecidos.current.map((i) => ({
            nfCompraProduto: i.nfCompraProduto,
            quantidade: inteira ? i.quantidadeNota : (qtds[i.nfCompraProduto] ?? i.quantidadeNota),
            valorUnitario: valores[i.nfCompraProduto] ?? null,
          }));
    return { valorBase, itens };
  }, [inteira, qtds, valores, valorBase]);
  const pedidoAtrasado = useAtrasado(pedido, 350);

  const previa = useQuery<RespostaPrevia>({
    queryKey: ['entrada-transf-previa', n.chave, pedidoAtrasado],
    queryFn: () => api.post(`/api/entradas/nota/${n.chave}/transferencia-nf/previa`, pedidoAtrasado),
    // Mantém a tabela na tela enquanto recalcula (sem piscar).
    placeholderData: (anterior) => anterior,
  });
  const p = previa.data?.previa;
  if (p) itensConhecidos.current = p.itens;
  const recalculando = previa.isFetching || pedido !== pedidoAtrasado;

  const emitir = useMutation({
    mutationFn: () => api.post<{ numero: number | null }>(`/api/entradas/nota/${n.chave}/transferencia-nf`, { ...pedido, totalConferido: p?.total }),
    onSuccess: async () => {
      // Espera o rastro chegar antes de soltar o botão: a troca montagem →
      // rastro acontece de uma vez, com a faixa de "emitida" (sem piscar).
      await cliente.invalidateQueries({ queryKey: ['entrada-transf', n.chave] });
      void cliente.invalidateQueries({ queryKey: ['entrada', n.chave] });
      void cliente.invalidateQueries({ queryKey: ['entradas'] });
    },
    onSettled: () => definirConfirmando(false),
  });
  const erro = emitir.error instanceof ErroHttp ? emitir.error.message : emitir.error ? String(emitir.error) : null;

  if (previa.isLoading) {
    return (
      <Secao titulo="NF de transferência">
        <Esqueleto className="h-24" />
      </Secao>
    );
  }
  if (previa.isError || !p) {
    const msg = previa.error instanceof ErroHttp ? previa.error.message : 'Não deu para montar a NF agora.';
    return (
      <Secao titulo="NF de transferência">
        <p className="rounded-lg bg-status-atencaoFundo px-3 py-2 text-[13px] text-status-atencao">{msg}</p>
      </Secao>
    );
  }

  const vao = p.itens.filter((i) => i.quantidade > 0);
  const podeEmitir = previa.data!.podeEmitir;
  const ligada = previa.data!.ligada;
  const bloqueado = p.bloqueios.length > 0;

  return (
    <Secao
      titulo="NF de transferência"
      direita={recalculando ? <Loader2 className="h-3.5 w-3.5 animate-spin text-textoSuave" aria-label="Recalculando" /> : null}
    >
      <Rota
        origem={{ empresa: p.empresaOrigem, fantasia: p.origem.fantasia, uf: p.ufOrigem, centro: p.centroSaida, centroNome: p.origem.centroNome }}
        destino={{
          empresa: p.destino.empresa,
          fantasia: p.destino.fantasia,
          nome: p.destino.nome,
          uf: p.destino.uf,
          tipo: p.destino.tipo,
          centro: p.destino.centro,
          centroNome: p.destino.centroNome,
        }}
        legenda={`NF ${p.operacao} → ${p.operacaoEntrada} · série ${p.serie}`}
      />
      <dl className="grid grid-cols-2 gap-x-4 gap-y-1.5 rounded-lg bg-superficie2 px-3 py-2 text-xs sm:grid-cols-4">
        <Fato rotulo="CFOP" valor={[...new Set(vao.map((i) => i.cfop).filter(Boolean))].join(', ') || '—'} />
        <Fato rotulo="Itens" valor={`${vao.length} de ${p.itens.length}`} />
        <Fato rotulo="Valor da NF" valor={moeda(p.total)} />
        <Fato rotulo="ICMS" valor={moeda(p.icmsTotal)} />
      </dl>
      <p className="text-2xs text-textoSuave">Operação, série e centro vêm do destino (Cadastro → Destinos). A tributação vem do grupo tributário de cada produto no PBS.</p>

      <div className="flex flex-wrap items-center gap-2">
        <div className="grid flex-1 grid-cols-2 gap-2" role="radiogroup" aria-label="Integral ou parcial">
          {(
            [
              [true, 'NF integral', `${p.itens.length} item(ns), todas as quantidades`],
              [false, 'Transferência parcial', 'Escolha item e quantidade'],
            ] as const
          ).map(([v, rotulo, ajuda]) => (
            <button
              key={rotulo}
              type="button"
              role="radio"
              aria-checked={inteira === v}
              onClick={() => {
                definirInteira(v);
                definirConfirmando(false);
              }}
              className={cn(
                'rounded-lg border px-3 py-2 text-left text-[13px] transition-colors',
                inteira === v ? 'border-primaria bg-primaria-suave' : 'border-borda hover:bg-superficie2',
              )}
            >
              <b>{rotulo}</b>
              <span className="block text-xs text-textoFraco">{ajuda}</span>
            </button>
          ))}
        </div>
        <div className="flex rounded-lg border border-borda p-0.5 text-xs" role="radiogroup" aria-label="Valor unitário">
          {(
            [
              ['compra_ipi', 'Compra + IPI'],
              ['compra', 'Só compra'],
            ] as const
          ).map(([v, rotulo]) => (
            <button
              key={v}
              type="button"
              role="radio"
              aria-checked={p.valorBase === v}
              className={cn('rounded-md px-2.5 py-1.5 font-semibold', p.valorBase === v ? 'bg-primaria text-white' : 'text-textoFraco hover:bg-superficie2')}
              onClick={() => {
                definirValorBase(v);
                definirValores({});
                definirConfirmando(false);
              }}
            >
              {rotulo}
            </button>
          ))}
        </div>
      </div>

      {!inteira ? (
        <div className="flex gap-2 text-xs">
          <button type="button" className={acaoTexto} onClick={() => definirQtds({})}>
            Tudo
          </button>
          <span className="text-textoSuave">·</span>
          <button
            type="button"
            className={acaoTexto}
            onClick={() => definirQtds(Object.fromEntries(p.itens.map((i) => [i.nfCompraProduto, 0])))}
          >
            Zerar todos
          </button>
        </div>
      ) : null}

      <div className="overflow-x-auto rounded-lg border border-borda">
        <table className="w-full text-xs">
          <thead className="bg-superficie2 text-left text-2xs uppercase tracking-wide text-textoSuave">
            <tr>
              <th className="px-2 py-1.5">Produto</th>
              <th className="px-2 py-1.5 text-right">Qtd</th>
              <th className="px-2 py-1.5 text-right">Unitário</th>
              <th className="px-2 py-1.5 text-right">Total</th>
              <th className="px-2 py-1.5 text-right" title="CST e alíquota de ICMS do grupo tributário">
                ICMS
              </th>
            </tr>
          </thead>
          <tbody>
            {p.itens.map((i) => (
              <LinhaItem
                key={i.nfCompraProduto}
                i={i}
                editarQtd={!inteira}
                valorMexido={valores[i.nfCompraProduto] != null}
                aoMudarQtd={(q) => {
                  definirQtds((x) => ({ ...x, [i.nfCompraProduto]: q }));
                  definirConfirmando(false);
                }}
                aoMudarValor={(v) => {
                  definirValores((x) => {
                    const novo = { ...x };
                    if (v == null) delete novo[i.nfCompraProduto];
                    else novo[i.nfCompraProduto] = v;
                    return novo;
                  });
                  definirConfirmando(false);
                }}
              />
            ))}
          </tbody>
          <tfoot className="border-t border-borda bg-superficie2 font-semibold">
            <tr>
              <td className="px-2 py-1.5">
                {vao.length} de {p.itens.length} item(ns)
              </td>
              <td />
              <td />
              <td className="px-2 py-1.5 text-right tabular-nums">{moeda(p.total)}</td>
              <td className="px-2 py-1.5 text-right tabular-nums">{moeda(p.icmsTotal)}</td>
            </tr>
          </tfoot>
        </table>
      </div>

      {p.bloqueios.map((b) => (
        <p key={b} className="flex gap-2 rounded-lg bg-status-divergenciaFundo px-3 py-2 text-[13px] text-status-divergencia">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" /> {b}
        </p>
      ))}
      {p.avisos.map((a) => (
        <p key={a} className="rounded-lg bg-status-atencaoFundo px-3 py-2 text-xs text-status-atencao">
          {a}
        </p>
      ))}

      {!ligada ? (
        <p className="rounded-lg bg-superficie2 px-3 py-2 text-xs text-textoFraco">
          A emissão pelo WMS está desligada (Cadastro → Regras → gerar NF de transferência). Use esta prévia para emitir no Procfit e vincule a entrada abaixo.
        </p>
      ) : !podeEmitir ? (
        <p className="text-xs text-textoFraco">Quem emite é o fiscal. Esta é a NF que vai sair.</p>
      ) : confirmando ? (
        <div className="space-y-2 rounded-lg border border-primaria/40 bg-primaria-suave/40 p-3">
          <p className="text-[13px]">
            Emitir a NF de transferência de <b>{moeda(p.total)}</b> ({vao.length} item(ns)) para <b>{p.destino.nome}</b>? Ela é numerada, baixa o estoque do
            centro {p.centroSaida} e vai para a SEFAZ — não dá para desfazer pelo WMS (cancelamento é no Procfit).
          </p>
          <div className="flex justify-end gap-2">
            <Botao variante="fantasma" tamanho="pequeno" onClick={() => definirConfirmando(false)} disabled={emitir.isPending}>
              Voltar
            </Botao>
            <Botao variante="primaria" tamanho="pequeno" carregando={emitir.isPending} disabled={recalculando} onClick={() => emitir.mutate()}>
              <Check className="h-4 w-4" /> Sim, emitir
            </Botao>
          </div>
        </div>
      ) : (
        <Botao variante="primaria" className="w-full" disabled={bloqueado || recalculando || !vao.length} onClick={() => definirConfirmando(true)}>
          <FileOutput className="h-4 w-4" />
          {bloqueado ? 'Resolva o que está em vermelho' : `Emitir NF de transferência · ${moeda(p.total)}`}
        </Botao>
      )}
      {erro ? <p className="rounded-lg bg-status-divergenciaFundo px-3 py-2 text-[13px] text-status-divergencia">{erro}</p> : null}
    </Secao>
  );
}

function LinhaItem({
  i,
  editarQtd,
  valorMexido,
  aoMudarQtd,
  aoMudarValor,
}: {
  i: ItemTransferencia;
  editarQtd: boolean;
  valorMexido: boolean;
  aoMudarQtd: (q: number) => void;
  aoMudarValor: (v: number | null) => void;
}): JSX.Element {
  // Texto local: o usuário digita "1538,8" sem o campo brigar com ele.
  const [valorTxt, definirValorTxt] = React.useState<string | null>(null);
  const fora = i.quantidade === 0;
  return (
    <tr className={cn('border-t border-borda align-top', fora && 'text-textoSuave', i.problema && i.quantidade > 0 && 'bg-status-divergenciaFundo/40')}>
      <td className="px-2 py-1.5">
        <span className="font-semibold tabular-nums">{i.produto}</span> <span className="line-clamp-1">{i.descricao}</span>
        {i.problema && i.quantidade > 0 ? <span className="block text-2xs text-status-divergencia">{i.problema}</span> : null}
      </td>
      <td className="px-2 py-1.5 text-right tabular-nums">
        {editarQtd ? (
          <input
            type="number"
            min={0}
            max={i.quantidadeNota}
            step="any"
            aria-label={`Quantidade do produto ${i.produto}`}
            className="h-8 w-16 rounded-md border border-borda bg-superficie px-1.5 text-right"
            value={i.quantidade}
            onChange={(e) => aoMudarQtd(Math.max(0, Math.min(i.quantidadeNota, Number(e.target.value) || 0)))}
          />
        ) : (
          num(i.quantidade, 0)
        )}
        {editarQtd ? <span className="block text-2xs text-textoSuave">de {num(i.saldo, 0)}</span> : null}
        {i.estoqueSaida != null && i.quantidade > 0 ? (
          <span
            className={cn('block whitespace-nowrap text-2xs', i.estoqueSaida < i.quantidade ? 'font-semibold text-status-divergencia' : 'text-textoSuave')}
            title="Estoque do ERP no centro de saída, já com o que chega com a compra"
          >
            estoque {num(i.estoqueSaida, 0)}
          </span>
        ) : null}
      </td>
      <td className="px-2 py-1.5 text-right tabular-nums">
        <input
          inputMode="decimal"
          aria-label={`Valor unitário do produto ${i.produto}`}
          title={
            i.valorCompra != null
              ? `Compra ${num(i.valorCompra, 4)} + IPI ${num(i.ipiUnitario, 4)} por unidade. Edite para usar outro valor.`
              : 'Sem valor na NF de compra: informe.'
          }
          className={cn(
            'h-8 w-24 rounded-md border bg-superficie px-1.5 text-right',
            valorMexido ? 'border-status-atencao font-semibold text-status-atencao' : 'border-transparent hover:border-borda focus:border-borda',
          )}
          value={valorTxt ?? (i.valorUnitario == null ? '' : num(i.valorUnitario, 4))}
          onFocus={(e) => e.target.select()}
          onChange={(e) => definirValorTxt(e.target.value)}
          onBlur={() => {
            if (valorTxt == null) return;
            const v = Number(valorTxt.replace(/\./g, '').replace(',', '.'));
            aoMudarValor(Number.isFinite(v) && v > 0 ? v : null);
            definirValorTxt(null);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
          }}
        />
        {valorMexido ? (
          <button type="button" className={cn(acaoTexto, 'mt-0.5 text-2xs')} onClick={() => aoMudarValor(null)}>
            <RotateCcw className="h-3 w-3" /> voltar ao da compra
          </button>
        ) : null}
      </td>
      <td className="px-2 py-1.5 text-right tabular-nums">{fora ? '—' : num(i.total)}</td>
      <td className="px-2 py-1.5 text-right tabular-nums">
        {fora ? (
          '—'
        ) : i.cst ? (
          <>
            {num(i.icmsValor)}
            <span className="block text-2xs text-textoSuave">
              CST {i.cst} · {num(i.aliquota ?? 0, i.aliquota && i.aliquota % 1 ? 1 : 0)}%
            </span>
          </>
        ) : (
          <span className="text-status-divergencia">sem regra</span>
        )}
      </td>
    </tr>
  );
}

// ------------------------------------------------------------------ rastro

type Passo = { titulo: string; estado: 'feito' | 'agora' | 'erro' | 'depois'; corpo?: React.ReactNode };

function Rastro({ d, t }: { d: DetalheNota; t: RastroTransferencia }): JSX.Element {
  const n = d.nota;
  const autorizada = t.status === 'AUTORIZADA' || t.status === 'ENTRADA' || t.status === 'DIVERGENTE';
  const quando = (iso: string | null): string =>
    iso ? new Date(iso).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '';
  const recebida = ['em_conferencia', 'divergencia_recebimento', 'estocada', 'disponivel_venda'].includes(n.status);
  // Recém-emitida: a montagem some e o rastro entra no lugar — sem esta faixa o
  // usuário via a tela "piscar" e não sabia se a NF tinha saído (29/10/2026).
  const recente = Date.now() - new Date(t.criadoEm).getTime() < 10 * 60_000;
  const faixa = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    if (recente) faixa.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    // Só ao abrir o rastro: o refetch de 15 s não rola a gaveta de novo.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const passos: Passo[] = [
    {
      titulo: `NF ${t.numero ?? '…'}/${t.serie ?? ''} gravada pelo WMS`,
      estado: 'feito',
      corpo: (
        <>
          {t.criadoPor} · {quando(t.criadoEm)} · {moeda(t.total)} · {t.notaInteira ? 'nota inteira' : 'parcial'} · centro {t.centroSaida} · op. {t.operacao}
        </>
      ),
    },
    {
      titulo: autorizada ? 'Autorizada na SEFAZ' : 'Aguardando a SEFAZ',
      estado: autorizada ? 'feito' : 'agora',
      corpo: autorizada ? (
        <>
          {quando(t.autorizadaEm)}
          {t.chaveNfe ? <ChaveCopiavel chave={t.chaveNfe} /> : null}
          <Documentos chave={n.chave} />
        </>
      ) : (
        <>O robô da NF-e transmite em poucos minutos. Esta tela confere sozinha.</>
      ),
    },
    {
      titulo:
        t.status === 'ENTRADA'
          ? `Entrada no destino: NF de compra ${t.entrada?.nfCompra ?? ''}`
          : t.status === 'DIVERGENTE'
            ? 'Entrada caiu na empresa errada'
            : 'Entrada no destino',
      estado: t.status === 'ENTRADA' ? 'feito' : t.status === 'DIVERGENTE' ? 'erro' : autorizada ? 'agora' : 'depois',
      corpo:
        t.status === 'DIVERGENTE' && t.entrada ? (
          <>
            O PBS deu entrada na empresa <b>{t.entrada.empresa}</b> (NF de compra {t.entrada.nfCompra}), mas {n.destinoNome} é de outra empresa. Corrija a empresa
            da entrada no Procfit: o WMS vincula sozinho quando bater.
          </>
        ) : t.entrada ? (
          <>
            Empresa {t.entrada.empresa}
            {t.entrada.centro ? ` · centro ${t.entrada.centro}` : ''}
            {t.entrada.quando ? ` · ${new Date(t.entrada.quando).toLocaleDateString('pt-BR')}` : ''} · operação de entrada pela mesma chave
          </>
        ) : autorizada ? (
          <>O WMS importa a entrada (op. 42) na empresa do destino logo depois da autorização. Esta tela confere sozinha.</>
        ) : null,
    },
    {
      titulo: recebida ? 'Recebida' : n.status === 'em_transito_full' ? 'Em trânsito para o Full' : 'Recebimento',
      estado: recebida ? 'feito' : t.status === 'ENTRADA' ? 'agora' : 'depois',
      corpo: t.status === 'ENTRADA' && !recebida ? <>Segue a esteira: confirme a chegada em {n.destinoNome}.</> : null,
    },
  ];

  return (
    <Secao titulo="NF de transferência">
      {recente ? (
        <div ref={faixa} className="flex items-start gap-2 rounded-lg bg-status-livreFundo px-3 py-2 text-[13px] text-status-livre">
          <Check className="mt-0.5 h-4 w-4 shrink-0" />
          <p>
            <b>
              NF de transferência {t.numero ?? '…'}/{t.serie ?? ''} emitida às{' '}
              {new Date(t.criadoEm).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
            </b>{' '}
            por {t.criadoPor}. {autorizada ? 'Já autorizada na SEFAZ.' : 'Aguardando a SEFAZ — esta tela confere sozinha.'}
          </p>
        </div>
      ) : null}
      <ol className="space-y-0">
        {passos.map((p, k) => (
          <li key={p.titulo} className="relative flex gap-3 pb-3 last:pb-0">
            {k < passos.length - 1 ? <span className="absolute left-[9px] top-5 h-full w-px bg-borda" aria-hidden /> : null}
            <span
              className={cn(
                'relative z-10 mt-0.5 flex h-[19px] w-[19px] shrink-0 items-center justify-center rounded-full border-2',
                p.estado === 'feito' && 'border-status-livre bg-status-livre text-white',
                p.estado === 'agora' && 'border-primaria bg-superficie',
                p.estado === 'erro' && 'border-status-divergencia bg-status-divergencia text-white',
                p.estado === 'depois' && 'border-borda bg-superficie',
              )}
            >
              {p.estado === 'feito' ? <Check className="h-3 w-3" /> : p.estado === 'erro' ? <AlertTriangle className="h-3 w-3" /> : null}
              {p.estado === 'agora' ? <span className="h-2 w-2 animate-pulse rounded-full bg-primaria" /> : null}
            </span>
            <div className="min-w-0 flex-1">
              <p className={cn('text-[13px] font-semibold', p.estado === 'depois' && 'text-textoSuave', p.estado === 'erro' && 'text-status-divergencia')}>{p.titulo}</p>
              {p.corpo ? <div className="mt-0.5 space-y-1.5 text-xs text-textoFraco">{p.corpo}</div> : null}
            </div>
          </li>
        ))}
      </ol>
      <details className="text-xs">
        <summary className="cursor-pointer font-semibold text-textoFraco hover:text-texto">O que foi na NF ({t.itens.length} item(ns))</summary>
        <table className="mt-2 w-full">
          <tbody>
            {t.itens.map((i) => (
              <tr key={i.produto} className="border-t border-borda">
                <td className="py-1 font-semibold tabular-nums">{i.produto}</td>
                <td className="py-1 text-right tabular-nums">{num(i.quantidade, 0)} ×</td>
                <td className="py-1 text-right tabular-nums">{num(i.valorUnitario, 4)}</td>
                <td className="py-1 text-right text-textoFraco">
                  CST {i.cst} · {i.aliquota}% · {num(i.icms)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </details>
    </Secao>
  );
}

function ChaveCopiavel({ chave }: { chave: string }): JSX.Element {
  const [copiou, definir] = React.useState(false);
  return (
    <button
      type="button"
      className="flex max-w-full items-center gap-1.5 font-mono text-2xs text-textoFraco hover:text-texto"
      title="Copiar a chave"
      onClick={() => {
        void navigator.clipboard?.writeText(chave).then(() => {
          definir(true);
          setTimeout(() => definir(false), 1500);
        });
      }}
    >
      <span className="truncate">{chave}</span>
      {copiou ? <Check className="h-3 w-3 shrink-0 text-status-livre" /> : <Copy className="h-3 w-3 shrink-0" />}
    </button>
  );
}

/** DANFE e XML da NF de transferência — a mesma chave é a da entrada no destino. */
function Documentos({ chave }: { chave: string }): JSX.Element {
  const base = `/api/entradas/nota/${chave}/transferencia-nf/arquivo`;
  const botao = 'inline-flex h-7 items-center gap-1 rounded-md border border-borda bg-superficie px-2 text-2xs font-semibold text-texto hover:border-bordaForte';
  return (
    <div className="flex flex-wrap gap-1.5">
      <a className={botao} href={`${base}/pdf`} target="_blank" rel="noopener noreferrer">
        <Printer className="h-3 w-3" /> DANFE
      </a>
      <a className={botao} href={`${base}/pdf?baixar=1`}>
        <Download className="h-3 w-3" /> Baixar
      </a>
      <a className={botao} href={`${base}/xml?baixar=1`}>
        <FileCode2 className="h-3 w-3" /> XML
      </a>
    </div>
  );
}

// ------------------------------------------------------------------ peças

function Secao({ titulo, direita, children }: { titulo: string; direita?: React.ReactNode; children: React.ReactNode }): JSX.Element {
  return (
    <section className="space-y-3 rounded-xl border border-borda p-4">
      <div className="flex items-center justify-between">
        <p className="rotulo">{titulo}</p>
        {direita}
      </div>
      {children}
    </section>
  );
}

function Fato({ rotulo, valor }: { rotulo: string; valor: React.ReactNode }): JSX.Element {
  return (
    <div className="min-w-0">
      <dt className="text-2xs font-semibold uppercase tracking-wide text-textoSuave">{rotulo}</dt>
      <dd className="truncate font-medium">{valor}</dd>
    </div>
  );
}
